import * as React from 'react';
import { useIntl } from 'react-intl';

import { Grid } from '@notino/react-styleguide';
import { getImageAttributes } from '@notino/react-web-utils';

import { ContentWrapper } from '@components';
import { Divider } from '@components/Divider';
import ErrorBoundary from '@components/ErrorBoundary';
import productDetailMessages from '@containers/ProductDetailContainer/messages';
import { Breadcrumb } from '@containers/ProductDetailContainer/ProductDetail/Breadcrumb';
import { HeadMeta } from '@containers/ProductDetailContainer/ProductDetail/HeadMeta';
import { getProductFullName } from '@containers/ProductDetailContainer/ProductDetail/ProductTabContent/utils';
import {
  IProductViewProviderProps,
  ProductViewProvider,
  useProductView,
} from '@context/product-view';
import useFeatureSwitches from '@hooks/useFeatureSwitches';

import { DESCRIPTION_MIN_LENGTH } from './constants';
import { Header } from './Header';
import { Reviews } from './Reviews';

const ReviewsPage = () => {
  const { newPdDesignEnabled, product, variant, data } = useProductView();
  const { reviewsOnServerIds } = useFeatureSwitches();
  const { formatMessage } = useIntl();

  const productFullName = getProductFullName(product);

  const annotation =
    variant.attributes && variant.attributes.Master
      ? product.annotation
      : variant.variantName;

  const imageAttrs = getImageAttributes(
    variant.imagesByCategory.mainImage,
    data.Settings.imageDomainPath,
    'detailMain'
  );

  const breadcrumbs = [
    ...product.breadcrumb,
    {
      name: formatMessage(productDetailMessages.reviewHeader, { name: '' }),
      link: null,
    },
  ];

  const description = product.description.split(/<p>(.*?)<\/p>/g)?.[1] || '';

  const simpleDescription =
    description.length > DESCRIPTION_MIN_LENGTH ? description : null;

  return (
    <>
      <ErrorBoundary>
        <HeadMeta product={product} variant={variant} />
      </ErrorBoundary>

      <ErrorBoundary>
        <ContentWrapper newPdDesignEnabled={newPdDesignEnabled}>
          <Grid fluid={true}>
            <Breadcrumb items={breadcrumbs} />

            <Header
              simpleDescription={simpleDescription}
              imageAttrs={imageAttrs}
              annotation={annotation}
              brandInfoName={productFullName}
              redirectUrl={product.url}
              newPdDesignEnabled={newPdDesignEnabled}
            />

            <Divider
              shouldShowOnMobile={true}
              newPdDesignEnabled={newPdDesignEnabled}
            />

            <Reviews
              renderReviewsOnServer={reviewsOnServerIds?.includes(product.id)}
              productCode={product.code}
              masterId={product.id}
              isAllReviewsPage={true}
            />
          </Grid>
        </ContentWrapper>
      </ErrorBoundary>
    </>
  );
};

const WrappedReviewsPage: React.FC<IProductViewProviderProps> = ({
  productId,
  ssrEnabled,
}) => {
  return (
    <ProductViewProvider productId={productId} ssrEnabled={ssrEnabled}>
      <ReviewsPage />
    </ProductViewProvider>
  );
};

export default WrappedReviewsPage;
